import { useCallback, useRef, useState, useEffect } from 'react';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';
import isFileTypeAccepted from '@/utils/file/isFileTypeAccepted';
import createPreviewUrl from '@/utils/media/createPreviewUrl';
import withJpegConversion from '@/utils/media/withJpegConversion';
import isAuthorized from '@/utils/user/isAuthorized';
import uploadToTempCDN from '@/requests/cloud-storage/uploadToTempCDN';

const HEIC_TYPES = ['image/heic', 'image/heif'];

interface UseFileUploadOptions {
  accept: string;
  value?: string | null;
  hasUploadError?: boolean;
  onUpload: (file: File) => boolean | Promise<boolean>;
  onRemove?: () => void;
  showInvalidFeedback: () => void;
  /**
   * Off for a caller with no slot of its own to render — the preset feed, whose
   * only media element is the one the store `src` drives. Re-encoding a pick
   * costs a full decode, so producing a preview nothing paints would just make
   * the upload slower. `hasPickedFile` still reports the file, so callers that
   * want the state but not the picture lose nothing.
   */
  withLocalPreview?: boolean;
}

/**
 * Hook to manage file upload logic including local preview and validation
 */
const useFileUpload = ({
  accept,
  value,
  hasUploadError,
  onUpload,
  onRemove,
  showInvalidFeedback,
  withLocalPreview = true,
}: UseFileUploadOptions) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  // A count, not a flag: a slide change in the preset feed can start a second
  // pick while the first upload is still running, and a boolean would let the
  // first one to settle report "nothing in hand" for the one still going.
  const [uploadsInFlight, setUploadsInFlight] = useState(0);

  // Drop the local preview once the CDN URL arrives or the upload fails.
  // Deliberately does NOT revoke here: at this point the DOM still points at
  // the blob, and only the commit that follows swaps it for `value`. Revoking
  // in this effect left a live `<img>` holding a dead url for a render — a
  // broken image waiting for a repaint. The cleanup below runs *after* that
  // commit, which is the right moment.
  useEffect(() => {
    if (localPreview && (value || hasUploadError)) {
      setLocalPreview(null);
    }
  }, [value, localPreview, hasUploadError]);

  // Frees each preview once nothing renders it any more: React runs this on the
  // commit where `localPreview` changed (or on unmount), by which point the DOM
  // has already moved to the CDN url.
  useEffect(() => {
    return () => {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    };
  }, [localPreview]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      // Preview FIRST, then upload. `onUpload` doesn't resolve until the CDN
      // upload finishes for a signed-in user (`useMediaUpload` awaits it), so
      // setting the preview afterwards meant the slot showed the untouched
      // dropzone for the entire upload — no preview, and no spinner either,
      // since `FilePreview` is the only thing that renders the input's
      // `isLoading` and it mounts only once there's something to preview.
      //
      // Kicked off before the preview is ready so the re-encode never delays
      // the upload itself.
      setUploadsInFlight((count) => count + 1);
      try {
        const uploading = onUpload(file);

        let localUrl: string | null = null;
        if (withLocalPreview) {
          // Display-sized, not the original bytes: a multi-MB `blob:` url in
          // the DOM breaks the host's DOM-mirroring agent (see
          // `appendix/troubleshooting.md`).
          localUrl = await createPreviewUrl(file);
          setLocalPreview(localUrl);

          // A signed-out pick isn't uploaded until login, and Chrome can't
          // render the local HEIC — show the CDN's JPEG of a temp copy instead.
          if (HEIC_TYPES.includes(file.type) && !isAuthorized()) {
            const blobUrl = localUrl;
            uploadToTempCDN(file)
              .then((url) =>
                setLocalPreview((current) =>
                  current === blobUrl ? withJpegConversion(url) : current
                )
              )
              .catch((error) =>
                errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
              );
          }
        }

        const accepted = await uploading;
        if (!accepted && localUrl) {
          URL.revokeObjectURL(localUrl);
          setLocalPreview(null);
        }
      } finally {
        setUploadsInFlight((count) => count - 1);
      }
    },
    [onUpload, withLocalPreview]
  );

  // Common validation and file processing logic used by both file chooser and drag-and-drop
  const validateAndProcessFile = useCallback(
    (file: File) => {
      if (!isFileTypeAccepted({ fileType: file.type, accept })) {
        // eslint-disable-next-line no-console
        console.warn(
          `[UploadArea] File type "${file.type}" not accepted. Expected: ${accept}`
        );
        showInvalidFeedback();
        return false;
      }
      handleFileSelect(file).catch((error) =>
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
      );
      return true;
    },
    [accept, showInvalidFeedback, handleFileSelect]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        validateAndProcessFile(file);
      }
      // Reset input so the same file can be selected again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [validateAndProcessFile]
  );

  const handleRemove = useCallback(() => {
    // Start removal animation
    setIsRemoving(true);

    // Wait for animation to complete, then clean up
    setTimeout(() => {
      // Clear local preview
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
        setLocalPreview(null);
      }
      setIsRemoving(false);
      // Call parent's onRemove
      onRemove?.();
    }, 150); // Match animation duration
  }, [localPreview, onRemove]);

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  // Use local preview if available, otherwise use CDN URL
  const previewUrl = localPreview || value;

  return {
    inputRef,
    previewUrl,
    // A file is in hand: still uploading, or already resolved to a preview or a
    // CDN url. Separate from `previewUrl` so it survives `withLocalPreview`
    // being off, where there is nothing to show but plenty to report.
    hasPickedFile: uploadsInFlight > 0 || Boolean(previewUrl),
    isRemoving,
    handleChange,
    handleRemove,
    openFilePicker,
    validateAndProcessFile,
  };
};

export default useFileUpload;
